import { useEffect, useState } from "react";
import { CheckCircle2, Clock, Eye, FileSignature, FileCheck2, ShieldAlert, UserCircle2, Wallet, ScrollText, AlertCircle } from "lucide-react";
import { EmployeeLayout } from "@/components/EmployeeLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DocumentViewDialog } from "@/components/DocumentViewDialog";
import { SignedFilePreviewDialog } from "@/components/SignedFilePreviewDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MyDataSection } from "./MyDataSection";
import { MySalarySection } from "./MySalarySection";
import { EXPEDIENTE_POLICIES, EXPEDIENTE_SIGNABLE_DOCS, EXPEDIENTE_TABS } from "@/constants/testIds";
import { toast } from "sonner";

const SIGNABLE_STATUS_META = {
  generated: { label: "Generado (aún no enviado)", className: "text-charcoal/40" },
  enviado_para_revision: { label: "Pendiente de tu revisión", className: "text-amber-500" },
  pendiente_firma_fisica: { label: "Pendiente de firma física con RH", className: "text-petroleum" },
  firmado_fisico_respaldo_digital: { label: "Firmado", className: "text-emerald-500" },
  confirmado_manual_admin: { label: "Confirmado por RH", className: "text-emerald-500" },
  reemplazado: { label: "Reemplazado por una versión nueva", className: "text-charcoal/40" },
};

export default function EmployeeExpediente() {
  const { user } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [signableDocs, setSignableDocs] = useState([]);
  const [viewedIds, setViewedIds] = useState([]);
  const [viewingAssignmentId, setViewingAssignmentId] = useState(null);
  const [viewingSignableDoc, setViewingSignableDoc] = useState(null);
  const [viewingSignedFile, setViewingSignedFile] = useState(null);

  const load = () => {
    if (!user?.employee_id) return;
    api.get(`/employees/${user.employee_id}`).then(({ data }) => setEmployee(data));
    api.get(`/employees/${user.employee_id}/signable-documents`).then(({ data }) => setSignableDocs(data));
  };

  useEffect(load, [user]);

  const acknowledge = async (assignmentId) => {
    try {
      await api.post(`/employees/${user.employee_id}/policies/${assignmentId}/acknowledge`);
      toast.success("Política confirmada");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markReviewed = async (docId) => {
    try {
      const { data } = await api.post(`/signable-documents/${docId}/mark-reviewed`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const viewSignedFile = (doc) => setViewingSignedFile(doc);

  if (!employee) return <EmployeeLayout><p className="text-charcoal/60">Cargando...</p></EmployeeLayout>;

  const employeePolicies = Array.isArray(employee.policies) ? employee.policies : [];
  const employeeIncidents = Array.isArray(employee.incidents) ? employee.incidents : [];
  const pendingPolicies = employeePolicies.filter((p) => p.status === "pending").length;
  const pendingDocs = signableDocs.filter((d) => d.status === "enviado_para_revision").length;

  return (
    <EmployeeLayout>
      <h1 className="font-heading text-2xl font-bold text-charcoal mb-1">Mi expediente</h1>
      <p className="text-sm text-charcoal/60 mb-5">{employee.position} · {employee.work_center}</p>

      <Tabs defaultValue="datos" className="w-full">
        <TabsList data-testid={EXPEDIENTE_TABS.tabList} className="bg-card w-full overflow-x-auto flex-nowrap justify-start h-auto p-1">
          <TabsTrigger data-testid={EXPEDIENTE_TABS.tabDatos} value="datos" className="flex items-center gap-1.5 shrink-0"><UserCircle2 className="w-3.5 h-3.5" /> Mis datos</TabsTrigger>
          <TabsTrigger data-testid={EXPEDIENTE_TABS.tabSalario} value="salario" className="flex items-center gap-1.5 shrink-0"><Wallet className="w-3.5 h-3.5" /> Mi salario</TabsTrigger>
          <TabsTrigger data-testid={EXPEDIENTE_TABS.tabPoliticas} value="politicas" className="flex items-center gap-1.5 shrink-0">
            <ScrollText className="w-3.5 h-3.5" /> Políticas {pendingPolicies > 0 && <span className="text-terracotta">({pendingPolicies})</span>}
          </TabsTrigger>
          <TabsTrigger data-testid={EXPEDIENTE_TABS.tabDocumentos} value="documentos" className="flex items-center gap-1.5 shrink-0">
            <FileSignature className="w-3.5 h-3.5" /> Documentos {pendingDocs > 0 && <span className="text-terracotta">({pendingDocs})</span>}
          </TabsTrigger>
          <TabsTrigger data-testid={EXPEDIENTE_TABS.tabIncidencias} value="incidencias" className="flex items-center gap-1.5 shrink-0"><AlertCircle className="w-3.5 h-3.5" /> Incidencias</TabsTrigger>
        </TabsList>

        <TabsContent value="datos" data-testid={EXPEDIENTE_TABS.contentDatos} className="mt-4">
          <MyDataSection employee={employee} employeeId={user.employee_id} companyName={user.company_name} />
        </TabsContent>

        <TabsContent value="salario" data-testid={EXPEDIENTE_TABS.contentSalario} className="mt-4">
          <MySalarySection currentSalary={employee.current_salary} compensationHistory={employee.compensation_history} />
        </TabsContent>

        <TabsContent value="politicas" data-testid={EXPEDIENTE_TABS.contentPoliticas} className="mt-4">
          <div className="space-y-2">
            {employeePolicies.map((p) => (
              <div key={p.assignment_id} className="bg-card border border-petroleum/10 rounded-2xl p-4">
                <div className="flex justify-between items-start">
                  <p className="font-medium text-charcoal text-sm">{p.title}</p>
                  {p.status === "acknowledged" ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  ) : (
                    <Clock className="w-4 h-4 text-amber-500 shrink-0" />
                  )}
                </div>
                {p.article_ref && <p className="text-xs font-mono-legal text-petroleum/70 mt-1">{p.article_ref}</p>}
                {p.status === "acknowledged" && p.manual && (
                  <p className="text-xs text-charcoal/50 mt-1">Confirmada manualmente por RH{p.manual_note ? `: "${p.manual_note}"` : ""}.</p>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <button
                    data-testid={`${EXPEDIENTE_POLICIES.viewButton}-${p.assignment_id}`}
                    onClick={() => setViewingAssignmentId(p.assignment_id)}
                    className="text-xs font-semibold text-petroleum flex items-center gap-1 underline"
                  >
                    <Eye className="w-3 h-3" /> Ver documento
                  </button>
                  {p.status === "pending" && (
                    <button
                      data-testid={`${EXPEDIENTE_POLICIES.ackButton}-${p.assignment_id}`}
                      onClick={() => acknowledge(p.assignment_id)}
                      disabled={!viewedIds.includes(p.assignment_id)}
                      title={!viewedIds.includes(p.assignment_id) ? "Primero abre y lee el documento completo" : ""}
                      className="text-xs font-semibold text-terracotta underline disabled:text-charcoal/30 disabled:no-underline disabled:cursor-not-allowed"
                    >
                      Confirmar lectura
                    </button>
                  )}
                </div>
              </div>
            ))}
            {employee.policies.length === 0 && <p className="text-sm text-charcoal/50">Sin políticas asignadas.</p>}
          </div>
        </TabsContent>

        <TabsContent value="documentos" data-testid={EXPEDIENTE_TABS.contentDocumentos} className="mt-4">
          <div data-testid={EXPEDIENTE_SIGNABLE_DOCS.section} className="space-y-2">
            {signableDocs.map((d) => (
              <div key={d.id} data-testid={`${EXPEDIENTE_SIGNABLE_DOCS.row}-${d.id}`} className="bg-card border border-petroleum/10 rounded-2xl p-4">
                <div className="flex justify-between items-start gap-2">
                  <p className="font-medium text-charcoal text-sm">{d.title}</p>
                  <span data-testid={EXPEDIENTE_SIGNABLE_DOCS.statusBadge} className={`text-xs font-semibold shrink-0 ${SIGNABLE_STATUS_META[d.status]?.className || "text-charcoal/50"}`}>
                    {SIGNABLE_STATUS_META[d.status]?.label || d.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {d.status !== "generated" && (
                    <button
                      data-testid={`${EXPEDIENTE_SIGNABLE_DOCS.viewButton}-${d.id}`}
                      onClick={() => setViewingSignableDoc(d.id)}
                      className="text-xs font-semibold text-petroleum flex items-center gap-1 underline"
                    >
                      <Eye className="w-3 h-3" /> Ver documento
                    </button>
                  )}
                  {d.status === "enviado_para_revision" && (
                    <button
                      data-testid={`${EXPEDIENTE_SIGNABLE_DOCS.markReviewedButton}-${d.id}`}
                      onClick={() => markReviewed(d.id)}
                      disabled={!viewedIds.includes(d.id)}
                      title={!viewedIds.includes(d.id) ? "Primero abre y lee el documento completo" : ""}
                      className="text-xs font-semibold text-terracotta underline disabled:text-charcoal/30 disabled:no-underline disabled:cursor-not-allowed"
                    >
                      Revisado, procederé a firmar en físico
                    </button>
                  )}
                  {(d.status === "firmado_fisico_respaldo_digital" || d.status === "reemplazado") && (
                    <button
                      data-testid={`${EXPEDIENTE_SIGNABLE_DOCS.viewSignedFileButton}-${d.id}`}
                      onClick={() => viewSignedFile(d)}
                      className="text-xs font-semibold text-emerald-600 flex items-center gap-1 underline"
                    >
                      <FileCheck2 className="w-3 h-3" /> Ver documento firmado
                    </button>
                  )}
                </div>
                {d.status === "pendiente_firma_fisica" && (
                  <p className="text-xs text-charcoal/50 mt-2 flex items-start gap-1"><ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" /> RH imprimirá el documento para firmarlo contigo en físico.</p>
                )}
                {d.status === "confirmado_manual_admin" && d.manual_confirmation && (
                  <p className="text-xs text-charcoal/50 mt-2">
                    Confirmado manualmente por RH ({d.manual_confirmation.by}){d.manual_confirmation.note ? `: "${d.manual_confirmation.note}"` : ""}.
                  </p>
                )}
              </div>
            ))}
            {signableDocs.length === 0 && <p className="text-sm text-charcoal/50">Sin documentos por firmar.</p>}
          </div>
        </TabsContent>

        <TabsContent value="incidencias" data-testid={EXPEDIENTE_TABS.contentIncidencias} className="mt-4">
          <div className="space-y-2">
            {employeeIncidents.map((inc) => (
              <div key={inc.id} className="bg-card border border-petroleum/10 rounded-2xl p-4">
                <p className="text-sm text-charcoal">{inc.description}</p>
                <p className="text-xs text-charcoal/50 mt-1">{inc.incident_date}</p>
              </div>
            ))}
            {employeeIncidents.length === 0 && <p className="text-sm text-charcoal/50">Sin incidencias registradas.</p>}
          </div>
        </TabsContent>
      </Tabs>

      <DocumentViewDialog
        open={!!viewingAssignmentId}
        onOpenChange={(v) => !v && setViewingAssignmentId(null)}
        fetchDocUrl={viewingAssignmentId ? `/employees/${user.employee_id}/policies/${viewingAssignmentId}/document` : null}
        pdfUrl={viewingAssignmentId ? `/employees/${user.employee_id}/policies/${viewingAssignmentId}/pdf` : null}
        onViewed={() => viewingAssignmentId && setViewedIds((prev) => (prev.includes(viewingAssignmentId) ? prev : [...prev, viewingAssignmentId]))}
      />
      <DocumentViewDialog
        open={!!viewingSignableDoc}
        onOpenChange={(v) => !v && setViewingSignableDoc(null)}
        fetchDocUrl={viewingSignableDoc ? `/signable-documents/${viewingSignableDoc}/document` : null}
        pdfUrl={viewingSignableDoc ? `/signable-documents/${viewingSignableDoc}/pdf` : null}
        onViewed={() => viewingSignableDoc && setViewedIds((prev) => (prev.includes(viewingSignableDoc) ? prev : [...prev, viewingSignableDoc]))}
      />
      <SignedFilePreviewDialog
        docId={viewingSignedFile?.id}
        title={viewingSignedFile?.title}
        open={!!viewingSignedFile}
        onOpenChange={(v) => !v && setViewingSignedFile(null)}
      />
    </EmployeeLayout>
  );
}


